import React, { useEffect, useRef } from 'react';
import { Avatar, Typography } from '@mui/material';

interface TechFigure {
  name: string;
  company: string;
  image: string;
  position: { x: number; y: number };
  delay: number;
}

const TechFigures: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const techFigures: TechFigure[] = [
    { name: 'Elon Musk', company: 'Tesla/SpaceX', image: '', position: { x: 10, y: 20 }, delay: 0 },
    { name: 'Tim Cook', company: 'Apple', image: '', position: { x: 30, y: 15 }, delay: 0.5 },
    { name: 'Satya Nadella', company: 'Microsoft', image: '', position: { x: 50, y: 25 }, delay: 1 },
    { name: 'Sundar Pichai', company: 'Google', image: '', position: { x: 70, y: 18 }, delay: 1.5 },
    { name: 'Jensen Huang', company: 'NVIDIA', image: '', position: { x: 20, y: 60 }, delay: 2 },
    { name: 'Reed Hastings', company: 'Netflix', image: '', position: { x: 40, y: 55 }, delay: 2.5 },
    { name: 'Brian Chesky', company: 'Airbnb', image: '', position: { x: 60, y: 62 }, delay: 3 },
    { name: 'Drew Houston', company: 'Dropbox', image: '', position: { x: 80, y: 58 }, delay: 3.5 },
    { name: 'Daniel Ek', company: 'Spotify', image: '', position: { x: 15, y: 85 }, delay: 4 },
    { name: 'Patrick Collison', company: 'Stripe', image: '', position: { x: 35, y: 80 }, delay: 4.5 },
    { name: 'Stewart Butterfield', company: 'Slack', image: '', position: { x: 55, y: 88 }, delay: 5 },
    { name: 'Evan Spiegel', company: 'Snapchat', image: '', position: { x: 75, y: 82 }, delay: 5.5 },
  ];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const animateFigures = () => {
      techFigures.forEach((figure, index) => {
        const element = container.children[index] as HTMLElement;
        if (element) {
          // Create floating animation
          const time = Date.now() * 0.001;
          const offsetX = Math.sin(time + figure.delay) * 10;
          const offsetY = Math.cos(time + figure.delay * 0.7) * 8;
          
          element.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
          element.style.transition = 'transform 0.1s ease-out';
        }
      });
    };

    const interval = setInterval(animateFigures, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      {techFigures.map((figure, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: `${figure.position.x}%`,
            top: `${figure.position.y}%`,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            opacity: 0.8,
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.8';
            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
          }}
        >
          <Avatar
            style={{
              width: 60,
              height: 60,
              border: '3px solid #F36409',
              boxShadow: '0 4px 12px rgba(243, 100, 9, 0.3)',
              backgroundColor: '#F36409',
              color: 'white',
              fontSize: '20px',
              fontWeight: 'bold',
            }}
            src={figure.image || undefined}
          >
            {figure.name.split(' ').map(n => n[0]).join('')}
          </Avatar>
          <Typography
            variant="caption"
            style={{
              color: 'white',
              fontWeight: 600,
              textAlign: 'center',
              marginTop: 8,
              fontSize: '0.7rem',
              maxWidth: 80,
            }}
          >
            {figure.name}
          </Typography>
          <Typography
            variant="caption"
            style={{
              color: 'white',
              textAlign: 'center',
              fontSize: '0.6rem',
              opacity: 0.8,
              maxWidth: 80,
            }}
          >
            {figure.company}
          </Typography>
        </div>
      ))}
    </div>
  );
};

export default TechFigures;
